"use client";

import { captureException } from "@web/lib/analytics/posthog-client";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSignUp, useSignIn } from "@/hooks/auth";
import { useAppAnalytics } from "@/hooks/analytics/use-app-analytics";
import { useSearchParams, useRouter } from "next/navigation";
import { Suspense } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@garagem/ui/components/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@garagem/ui/components/form";
import { Input } from "@garagem/ui/components/input";
import { PasswordInput } from "@garagem/ui/components/password-input";
import { PhoneInput } from "@garagem/ui/components/shared/phone-input";
import { Button } from "@garagem/ui/components/button";
import Link from "next/link";
import { toast } from "@garagem/ui/components/sonner";
import { authRedirectWithSession, getReturnUrl } from "@/lib/auth/redirect";
import { internalAuthPath } from "@garagem/foundation-shared/lib/auth/safe-return-path";
import { authPathWithSearch } from "@web/lib/auth/strip-credential-search-params";
import { asErrorChainNode } from "@garagem/foundation-shared/lib/errors/chain";
import { PHONE_OWNED_BY_ANOTHER_ACCOUNT_MESSAGE } from "@garagem/foundation-shared/lib/auth/account-identity-copy";
import {
  DISPOSABLE_SIGNUP_EMAIL_MESSAGE,
  SIGNUP_EMAIL_NO_MX_MESSAGE,
} from "@garagem/foundation-shared/lib/auth/signup-email-messages";
import { SignUpLegalConsentFields } from "@/components/auth/sign-up-legal-consent-fields";

const requiredConsent = (message: string) =>
  z.boolean().refine((value) => value, { message });

// Define the form schema using Zod
const schema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  email: z.email("Email inválido"),
  phone: z.string().regex(/^\+[1-9]\d{7,14}$/, "Telefone inválido"),
  password: z.string().min(8, "A senha deve ter pelo menos 8 caracteres"),
  acceptTerms: requiredConsent("Aceite os Termos de Uso"),
  acceptPrivacy: requiredConsent("Aceite a Política de Privacidade"),
  acceptAiTransparency: requiredConsent("Aceite a Transparência de IA"),
});

type FormData = z.infer<typeof schema>;

interface SignUpFormProps {
  /**
   * Called when signup fails because the email already has an account
   * (`USER_ALREADY_EXISTS`). Lets the invitation flow flip to the sign-in tab
   * in place instead of leaving the user on a dead-end "email já cadastrado"
   * error. When omitted (standalone `/cadastrar`), the form navigates to
   * `/entrar` with the email prefilled.
   */
  onAccountExists?: () => void;
}

function SignUpFormInner({ onAccountExists }: SignUpFormProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { trackFormSubmit } = useAppAnalytics();

  // React Query hooks
  const signUp = useSignUp();
  const signIn = useSignIn();

  // Get email and return URL from URL params
  const emailFromUrl = searchParams?.get("email");
  const isInvitationFlow = !!(
    searchParams?.get("invitation") === "true" || 
    searchParams?.get("from")?.includes("aceitar-convite")
  );
  
  // Landing page parameters
  const planParam = searchParams?.get("plan");
  const capabilitiesParam = searchParams?.get("capabilities");
  const billingParam = searchParams?.get("billing");
  const sourceParam = searchParams?.get("source");
  const campaignParam = searchParams?.get("campaign");
  const refParam = searchParams?.get("ref");
  const packageGenerationsParam = searchParams?.get("packageGenerations");
  const packageContractsParam = searchParams?.get("packageContracts");
  const packageTermsParam = searchParams?.get("packageTerms");

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: emailFromUrl || "",
      phone: "",
      password: "",
      acceptTerms: false,
      acceptPrivacy: false,
      acceptAiTransparency: false,
    },
  });

  // Update form when email param changes
  useEffect(() => {
    if (emailFromUrl) {
      form.setValue("email", emailFromUrl);
    }
    
    // Store landing page parameters for organization creation
    if (
      planParam ||
      capabilitiesParam ||
      sourceParam ||
      campaignParam ||
      refParam ||
      packageGenerationsParam ||
      packageContractsParam ||
      packageTermsParam
    ) {
      const onboardingData = {
        plan: planParam,
        capabilities: capabilitiesParam,
        billing: billingParam,
        source: sourceParam,
        campaign: campaignParam,
        referralCodeRaw: refParam ?? undefined,
        packageGenerations: packageGenerationsParam,
        packageContracts: packageContractsParam,
        packageTerms: packageTermsParam,
        timestamp: new Date().toISOString(),
      };
      sessionStorage.setItem("onboarding_source", JSON.stringify(onboardingData));
      try {
        const existing = localStorage.getItem("onboarding_params");
        const merged = existing
          ? { ...JSON.parse(existing), referralCodeRaw: refParam ?? undefined }
          : { referralCodeRaw: refParam ?? undefined, timestamp: new Date().toISOString() };
        localStorage.setItem("onboarding_params", JSON.stringify(merged));
      } catch {
        // ignore
      }
    }
  }, [
    emailFromUrl,
    form,
    planParam,
    capabilitiesParam,
    billingParam,
    sourceParam,
    campaignParam,
    refParam,
    packageGenerationsParam,
    packageContractsParam,
    packageTermsParam,
  ]);

  const handleSubmit = async (formData: FormData) => {
    try {
      // Sign up the user
      await signUp.mutateAsync({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        acceptTerms: true,
        acceptPrivacy: true,
        acceptAiTransparency: true,
      });

      trackFormSubmit("account_sign_up", true, undefined, {
        phone: formData.phone,
      });

      // One OTP only: `/verificar-telefone` auto-sends. A second send here
      // would replace the code before the user can type the first one.
      //
      // Regular signup must not call signIn.email: requireEmailVerification
      // skips autoSignIn and then sign-in throws EMAIL_NOT_VERIFIED, so there
      // is still no session. The signup after-hook sets the cookie for a real
      // new user. Invitations auto-verify email in that same hook, then sign
      // in to replace the cookie before the phone gate.
      if (isInvitationFlow) {
        toast.success("Conta criada com sucesso!", {
          description: "Fazendo login e verificando o WhatsApp...",
        });

        await signIn.mutateAsync({
          email: formData.email,
          password: formData.password,
        });
      } else {
        toast.success("Conta criada!", {
          description: "Confirme o código enviado no WhatsApp para continuar.",
        });
      }

      // Live query: the invite lives in `from` on /entrar and must be copied
      // into `returnUrl` at submit, not from a render that had not seen it.
      const returnUrl = getReturnUrl(
        new URLSearchParams(
          window.location.search || searchParams?.toString(),
        ),
      );
      const phoneUrl = new URL("/verificar-telefone", window.location.origin);
      phoneUrl.searchParams.set("email", formData.email);
      if (returnUrl !== "/") {
        phoneUrl.searchParams.set("returnUrl", returnUrl);
      }
      if (planParam) phoneUrl.searchParams.set("plan", planParam);
      if (capabilitiesParam) {
        phoneUrl.searchParams.set("capabilities", capabilitiesParam);
      }
      if (billingParam) phoneUrl.searchParams.set("billing", billingParam);
      if (sourceParam) phoneUrl.searchParams.set("source", sourceParam);
      if (campaignParam) phoneUrl.searchParams.set("campaign", campaignParam);
      if (refParam) phoneUrl.searchParams.set("ref", refParam);
      if (packageGenerationsParam) {
        phoneUrl.searchParams.set("packageGenerations", packageGenerationsParam);
      }
      if (packageContractsParam) {
        phoneUrl.searchParams.set("packageContracts", packageContractsParam);
      }
      if (packageTermsParam) {
        phoneUrl.searchParams.set("packageTerms", packageTermsParam);
      }

      await authRedirectWithSession(internalAuthPath(phoneUrl));
    } catch (err: unknown) {
      captureException(err, { source: "sign-up-form", component: "SignUpForm", action: "sign-up" });

      const error = asErrorChainNode(err);

      trackFormSubmit(
        "account_sign_up",
        false,
        typeof error.message === "string" ? error.message : "unknown_error"
      );

      if (
        error.message?.includes("already exists") ||
        error.code === "USER_ALREADY_EXISTS"
      ) {
        // Returning user: an account already exists for this email (common for
        // re-invited former members). Don't dead-end them on an error — steer
        // them to sign in and reset their password.
        toast.info("Você já tem uma conta", {
          description:
            'Entre com sua senha. Se esqueceu, use "Esqueceu sua senha?" para redefinir.',
        });
        if (onAccountExists) {
          onAccountExists();
        } else {
          const params = new URLSearchParams(searchParams?.toString() || "");
          params.set("email", formData.email);
          router.push(authPathWithSearch("/entrar", params));
        }
      } else if (
        error.code === "DISPOSABLE_EMAIL" ||
        error.message === DISPOSABLE_SIGNUP_EMAIL_MESSAGE
      ) {
        toast.error("E-mail temporário", {
          description: DISPOSABLE_SIGNUP_EMAIL_MESSAGE,
        });
      } else if (
        error.code === "EMAIL_DOMAIN_NO_MX" ||
        error.message === SIGNUP_EMAIL_NO_MX_MESSAGE
      ) {
        toast.error("E-mail inválido", {
          description: SIGNUP_EMAIL_NO_MX_MESSAGE,
        });
      } else if (
        error.code === "PHONE_OWNED_BY_ANOTHER_ACCOUNT" ||
        error.message === PHONE_OWNED_BY_ANOTHER_ACCOUNT_MESSAGE
      ) {
        toast.error("Telefone já usado", {
          description: PHONE_OWNED_BY_ANOTHER_ACCOUNT_MESSAGE,
        });
      } else if (error.message?.toLowerCase().includes("password") || error.code?.includes("PASSWORD")) {
        const passwordDescriptions: Record<string, string> = {
          PASSWORD_TOO_SHORT: "A senha deve ter pelo menos 8 caracteres.",
          PASSWORD_TOO_LONG: "A senha é muito longa (máximo 128 caracteres).",
          INVALID_PASSWORD: "Senha inválida. Verifique e tente novamente.",
        };
        toast.error("Senha inválida", {
          description: (error.code ? passwordDescriptions[error.code] : undefined) || error.message || "Verifique sua senha e tente novamente.",
        });
      } else {
        toast.error("Erro ao criar conta", {
          description:
            error.message ||
            "Ocorreu um erro ao criar sua conta. Tente novamente.",
        });
      }
    }
  };

  // Loading state from either mutation
  const isLoading = signUp.isPending || signIn.isPending;

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Criar Conta</CardTitle>
        <CardDescription>
          Preencha as informações abaixo para criar sua conta
        </CardDescription>
      </CardHeader>
      <CardContent>

        <Form {...form}>
          <form
            method="post"
            onSubmit={form.handleSubmit(handleSubmit)}
            id="sign-up-form"
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Completo</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="João Silva"
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email
                    {isInvitationFlow && emailFromUrl && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (bloqueado para o convite)
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      autoComplete="email"
                      placeholder="seu@email.com"
                      disabled={isLoading}
                      readOnly={isInvitationFlow && !!emailFromUrl}
                      className={isInvitationFlow && emailFromUrl ? "bg-muted cursor-not-allowed" : ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      disabled={isLoading}
                      placeholder="(00) 00000-0000"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      autoComplete="new-password"
                      placeholder="Mínimo 8 caracteres"
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SignUpLegalConsentFields control={form.control} disabled={isLoading} />
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? "Criando conta..." : "Criar conta"}
            </Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter>
        <div className="text-center text-sm w-full">
          Já tem uma conta?{" "}
          <Link
            href={authPathWithSearch("/entrar", searchParams)}
            className="underline"
          >
            Entrar
          </Link>
        </div>
      </CardFooter>
    </Card>
  );
}

export function SignUpForm({ onAccountExists }: SignUpFormProps = {}) {
  return (
    <Suspense fallback={null}>
      <SignUpFormInner onAccountExists={onAccountExists} />
    </Suspense>
  );
}
